/**
 * ErrorBoundary.tsx
 *
 * Global error boundary that catches React errors, displays a friendly error UI,
 * and automatically submits crash reports to the bug tracker API.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { apiClient } from '@/base/utils/api';

// Session log types
interface SessionLog {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  data?: unknown;
}

// Browser info captured at crash time
interface BrowserInfo {
  userAgent: string;
  platform: string;
  language: string;
  cookiesEnabled: boolean;
  viewport: { width: number; height: number };
  url: string;
  referrer: string;
  timestamp: string;
  timezone: string;
}

// Network error captured during session
interface NetworkError {
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  timestamp: string;
  errorMessage?: string;
}

// Crash report payload sent to API
interface CrashReportPayload {
  title: string;
  description: string;
  severity: 'high' | 'critical';
  type: 'crash';
  pageUrl?: string;
  componentName?: string;
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
  sessionLogs?: SessionLog[];
  browserInfo?: BrowserInfo;
  consoleErrors?: Record<string, unknown>[];
  networkErrors?: NetworkError[];
  stateSnapshot?: Record<string, unknown>;
  environment?: string;
  appVersion?: string;
  userAgent?: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  crashReportId: string | null;
  isSubmitting: boolean;
  submitError: string | null;
}

// Global session logs (captured from console)
const sessionLogs: SessionLog[] = [];
const consoleErrors: Record<string, unknown>[] = [];
const networkErrors: NetworkError[] = [];
const MAX_LOGS = 100;

// Initialize console capturing
function initConsoleCapture() {
  if (typeof window === 'undefined') return;

  const originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug,
  };

  const captureLog = (level: SessionLog['level']) => (...args: unknown[]): void => {
    // Call original console method
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    originalConsole[level](...(args as any[]));

    // Capture to session logs
    const log: SessionLog = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg => {
        try {
          return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
        } catch {
          return '[Unserializable]';
        }
      }).join(' '),
    };

    sessionLogs.push(log);
    if (sessionLogs.length > MAX_LOGS) {
      sessionLogs.shift();
    }

    // Capture errors separately
    if (level === 'error') {
      consoleErrors.push({
        timestamp: log.timestamp,
        message: log.message,
        args: args.map(arg => {
          if (arg instanceof Error) {
            return { name: arg.name, message: arg.message, stack: arg.stack };
          }
          return arg;
        }),
      });
      if (consoleErrors.length > 20) {
        consoleErrors.shift();
      }
    }
  };

  // Override console methods
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.log = captureLog('log') as (...data: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.warn = captureLog('warn') as (...data: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.error = captureLog('error') as (...data: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.info = captureLog('info') as (...data: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.debug = captureLog('debug') as (...data: any[]) => void;
}

// Initialize network error capturing
function initNetworkCapture() {
  if (typeof window === 'undefined') return;

  // Capture fetch errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const [input, init] = args;
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
    const method = init?.method || 'GET';

    try {
      const response = await originalFetch(...args);

      // Capture 4xx and 5xx errors
      if (!response.ok) {
        networkErrors.push({
          url,
          method,
          status: response.status,
          statusText: response.statusText,
          timestamp: new Date().toISOString(),
        });
        if (networkErrors.length > 20) {
          networkErrors.shift();
        }
      }

      return response;
    } catch (error) {
      networkErrors.push({
        url,
        method,
        timestamp: new Date().toISOString(),
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      if (networkErrors.length > 20) {
        networkErrors.shift();
      }
      throw error;
    }
  };
}

// Initialize capturing on module load
if (typeof window !== 'undefined') {
  initConsoleCapture();
  initNetworkCapture();
}

// Get browser info
function getBrowserInfo(): BrowserInfo {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// Submit crash report to API
async function submitCrashReport(payload: CrashReportPayload): Promise<{ bugReport: { id: string } }> {
  try {
    const response = await apiClient.post<{ bugReport: { id: string } }>('/v1/bug-reports/crash', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to submit crash report:', error);
    throw error;
  }
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      crashReportId: null,
      isSubmitting: false,
      submitError: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Auto-submit crash report
    this.submitCrashReport(error, errorInfo);
  }

  async submitCrashReport(error: Error, errorInfo: ErrorInfo) {
    this.setState({ isSubmitting: true, submitError: null });

    try {
      const payload: CrashReportPayload = {
        title: `Crash: ${error.message.slice(0, 100)}`,
        description: `Application crashed with error: ${error.message}\n\nThis crash report was automatically captured by the Error Boundary.`,
        severity: 'high',
        type: 'crash',
        pageUrl: window.location.href,
        componentName: errorInfo.componentStack?.split('\n')[1]?.trim() || undefined,
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo.componentStack || undefined,
        sessionLogs: [...sessionLogs],
        browserInfo: getBrowserInfo(),
        consoleErrors: [...consoleErrors],
        networkErrors: [...networkErrors],
        environment: import.meta.env.MODE || 'production',
        appVersion: import.meta.env.VITE_APP_VERSION || '0.1.0',
        userAgent: navigator.userAgent,
      };

      const result = await submitCrashReport(payload);
      this.setState({ crashReportId: result.bugReport.id, isSubmitting: false });
    } catch (err) {
      this.setState({
        isSubmitting: false,
        submitError: err instanceof Error ? err.message : 'Failed to submit crash report',
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo, crashReportId, isSubmitting, submitError } = this.state;

    if (hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconContainer}>
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h1 style={styles.title}>Something went wrong</h1>

            <p style={styles.message}>
              The application encountered an unexpected error. Our team has been notified and is working to fix the issue.
            </p>

            {/* Crash report status */}
            <div style={styles.reportStatus}>
              {isSubmitting && (
                <p style={styles.statusText}>
                  <span style={styles.spinner}></span> Submitting crash report...
                </p>
              )}
              {crashReportId && (
                <p style={styles.statusSuccess}>
                  Crash report submitted (ID: {crashReportId.slice(0, 8)}...)
                </p>
              )}
              {submitError && (
                <p style={styles.statusError}>
                  Could not submit crash report: {submitError}
                </p>
              )}
            </div>

            {/* Error details — shown in all environments for debugging */}
            {error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details</summary>
                <pre style={styles.errorText}>
                  <strong>Error:</strong> {error.message}
                  {'\n\n'}
                  <strong>Stack:</strong> {error.stack}
                  {errorInfo?.componentStack && (
                    <>
                      {'\n\n'}
                      <strong>Component Stack:</strong> {errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            {/* Action buttons */}
            <div style={styles.actions}>
              <button style={styles.buttonPrimary} onClick={this.handleReload}>
                Reload Page
              </button>
              <button style={styles.buttonSecondary} onClick={this.handleGoHome}>
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inline styles for the error UI
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    backgroundColor: '#0f172a',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  card: {
    maxWidth: '560px',
    width: '100%',
    padding: '40px',
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  icon: {
    width: '64px',
    height: '64px',
    color: '#f59e0b',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#f1f5f9',
    marginBottom: '16px',
    marginTop: 0,
  },
  message: {
    fontSize: '16px',
    color: '#94a3b8',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  reportStatus: {
    marginBottom: '24px',
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: '#0f172a',
  },
  statusText: {
    color: '#94a3b8',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  statusSuccess: {
    color: '#22c55e',
    margin: 0,
  },
  statusError: {
    color: '#ef4444',
    margin: 0,
    fontSize: '14px',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid #64748b',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  details: {
    textAlign: 'left',
    marginBottom: '24px',
  },
  summary: {
    cursor: 'pointer',
    color: '#64748b',
    fontSize: '14px',
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '12px',
    color: '#ef4444',
    backgroundColor: '#1e1e2e',
    padding: '16px',
    borderRadius: '8px',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    textAlign: 'left',
    margin: 0,
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  buttonPrimary: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonSecondary: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#94a3b8',
    backgroundColor: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

// Add keyframe animation for spinner via style tag
if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleTag);
}

export default ErrorBoundary;
